"use client"

import type React from "react"
import type { IconType } from "react-icons"

interface PageHeaderAction {
  label: string
  onClick: () => void
  icon?: IconType
  variant?: "primary" | "secondary" | "success"
  disabled?: boolean
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  icon: IconType
  actionLabel?: string
  onAction?: () => void
  actionIcon?: IconType
  // Nueva prop para múltiples acciones
  actions?: PageHeaderAction[]
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  actionLabel,
  onAction,
  actionIcon: ActionIcon,
  actions,
}) => {
  const getButtonStyles = (variant: PageHeaderAction["variant"] = "primary") => {
    const baseStyles = "px-4 py-2 rounded-lg transition-all duration-200 flex items-center shadow-md"
    
    switch (variant) {
      case "primary":
        return `${baseStyles} bg-[#2d53da] hover:bg-[#1e3bb8] text-white`
      case "secondary":
        return `${baseStyles} bg-gray-600 hover:bg-gray-700 text-white`
      case "success":
        return `${baseStyles} bg-green-600 hover:bg-green-700 text-white`
      default:
        return `${baseStyles} bg-[#2d53da] hover:bg-[#1e3bb8] text-white`
    }
  }

  return (
    <div className="flex flex-col md:flex-row justify-between items-center mb-6">
      <div className="flex items-center mb-4 md:mb-0">
        <div className="bg-[#2d53da] p-3 rounded-lg text-white mr-4">
          <Icon className="text-xl" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
          {subtitle && <p className="text-gray-500">{subtitle}</p>}
        </div>
      </div>
      
      <div className="flex gap-3">
        {/* Renderizar múltiples acciones si existen */}
        {actions && actions.length > 0 ? (
          actions.map((action, index) => {
            const ButtonIcon = action.icon
            return (
              <button
                key={index}
                onClick={action.onClick}
                disabled={action.disabled}
                className={`${getButtonStyles(action.variant)} ${
                  action.disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {ButtonIcon && <ButtonIcon className="mr-2" />}
                {action.label}
              </button>
            )
          })
        ) : (
          /* Mantener compatibilidad con la prop antigua */
          actionLabel && onAction && (
            <button
              onClick={onAction}
              className={getButtonStyles("primary")}
            >
              {ActionIcon && <ActionIcon className="mr-2" />}
              {actionLabel}
            </button>
          )
        )}
      </div>
    </div>
  )
}

export default PageHeader